import React from 'react';
import { MaskIcon } from './Icon';
import './AmountKeypad.css';

interface AmountKeypadProps {
  amount: string; // raw string "2950" → 29,50€
  onDigit: (d: string) => void;
  onDot: () => void;
  onDelete: () => void;
  onClear: () => void;
  onValidate: () => void;
  onScan?: () => void;
  label?: string;
  validateDisabled?: boolean;
  clientName?: string;
  clientBalance?: number;
  methodToggle?: React.ReactNode;
}

function formatAmount(raw: string): string {
  if (!raw || raw === '0') return '0,00';
  // Si pas de point décimal, on considère les centimes
  if (raw.includes('.')) {
    const [euros, cents = ''] = raw.split('.');
    return `${parseInt(euros || '0').toLocaleString('fr-FR')},${cents.padEnd(2, '0').slice(0, 2)}`;
  }
  const n = parseInt(raw);
  const euros = Math.floor(n / 100);
  const cents = n % 100;
  return `${euros.toLocaleString('fr-FR')},${String(cents).padStart(2, '0')}`;
}

export function AmountKeypad({
  amount, onDigit, onDot, onDelete, onClear, onValidate, onScan,
  label = 'Montant', validateDisabled = false, clientName, clientBalance,
  methodToggle,
}: AmountKeypadProps) {
  return (
    <div className="keypad">
      {/* Amount display */}
      <div className="keypad-display">
        <span className="keypad-label">{label}</span>
        <div className="keypad-amount">
          <span className="keypad-value">{formatAmount(amount)}</span>
          <span className="keypad-currency">€</span>
        </div>
      </div>

      {/* Method toggle (optional) */}
      {methodToggle && <div className="keypad-method">{methodToggle}</div>}

      {/* Client banner (optional) */}
      {clientName && (
        <div className="keypad-client">
          <div className="keypad-client-avatar">
            {clientName.charAt(0).toUpperCase()}
          </div>
          <div className="keypad-client-info">
            <strong>{clientName}</strong>
            {clientBalance !== undefined && (
              <span className="keypad-client-balance">Cagnotte {(clientBalance / 100).toFixed(2).replace('.', ',')}€</span>
            )}
          </div>
        </div>
      )}

      {/* Keypad grid */}
      <div className="keypad-grid">
        <button className="keypad-key" onClick={() => onDigit('1')}>1</button>
        <button className="keypad-key" onClick={() => onDigit('2')}>2</button>
        <button className="keypad-key" onClick={() => onDigit('3')}>3</button>
        <button className="keypad-key keypad-scan" onClick={onScan}>
          <MaskIcon name="qrcode" size={28} color="#666" />
        </button>

        <button className="keypad-key" onClick={() => onDigit('4')}>4</button>
        <button className="keypad-key" onClick={() => onDigit('5')}>5</button>
        <button className="keypad-key" onClick={() => onDigit('6')}>6</button>
        <button className="keypad-key keypad-clear" onClick={onClear}>
          <MaskIcon name="xmark" size={28} color="white" />
        </button>

        <button className="keypad-key" onClick={() => onDigit('7')}>7</button>
        <button className="keypad-key" onClick={() => onDigit('8')}>8</button>
        <button className="keypad-key" onClick={() => onDigit('9')}>9</button>
        <button className="keypad-key keypad-delete" onClick={onDelete}>
          <MaskIcon name="chevron-left" size={28} color="white" />
        </button>

        <button className="keypad-key keypad-fn">F</button>
        <button className="keypad-key" onClick={() => onDigit('0')}>0</button>
        <button className="keypad-key" onClick={onDot}>.</button>
        <button className="keypad-key keypad-validate" onClick={onValidate} disabled={validateDisabled}>
          <MaskIcon name="check" size={28} color="white" />
        </button>
      </div>
    </div>
  );
}
